import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString } from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { BrandGroupMemberModel } from '@models/types'

const watchEntityKeys = ['brandGroupMembers']

interface BrandGroupFilters {
  brand_group_id?: number,
  member_id?: number,
  string?: string,
}

interface Options {
  entityKey?: string,
  filters?: BrandGroupFilters,
  performHttpRequests?: boolean,
  requestOptions?: {
    [key: string]: boolean,
  },
}

function useBrandGroupMembers(options: Options = {}) {
  const { entityKey, filters, requestOptions } = options

  const {
    brand_group_id: filterBrandGroupId,
    string: filterString,
  } = filters || {}

  const {
    updatedEntities: { brandGroupMembers: brandGroupMembersUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const brandGroupMembers = useSelector(reduxState => reduxState.entities.brandGroupMembers)

  const filteredBrandGroupMembers = useMemo((): BrandGroupMemberModel[] => {
    const filtered = Object.values(brandGroupMembers).filter((brandGroupMember) => {
      const { brand_group_id, member } = brandGroupMember
      const { title } = member || {}

      const matchBrandGroup: boolean = matchFilterNumber(brand_group_id, filterBrandGroupId)
      const matchProviderTitle: boolean = matchFilterString(title, filterString)

      return matchBrandGroup && matchProviderTitle
    })

    const sorted = filtered.sort((brandGroupMember1, brandGroupMember2) => {
      const comparisonA = Number(brandGroupMember2.admin) - Number(brandGroupMember1.admin)
      // @ts-expect-error TS18048
      const comparisonB = brandGroupMember2.member?.title > brandGroupMember1.member?.title ? -1 : 1

      return comparisonA || comparisonB
    })

    return sorted
  }, [brandGroupMembersUpdatedAt, JSON.stringify(filters)])

  const filteredBrandGroupMemberIds = filteredBrandGroupMembers.map(brandGroupMember => brandGroupMember.member_id)
  const filteredBrandGroupMembersCount = filteredBrandGroupMembers.length
  const hasFilteredBrandGroupMembers = !!filteredBrandGroupMembersCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredBrandGroupMembersCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingBrandGroupMembers } = useReduxAction(
    'brandGroupMembers',
    'loadBrandGroupMembers',
    {
      entityKey,
      limit,
      ...requestOptions,
      ...filtersWithOffset,
    },
    [filtersWithOffset, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredBrandGroupMembers,
    filteredBrandGroupMembersCount,
    hasFilteredBrandGroupMembers,
    filteredBrandGroupMemberIds,
    loading: loadingBrandGroupMembers,
  }
}

export default useBrandGroupMembers
